import React, { useMemo, useState } from 'react';
import { VehiculeConfig, Service, PersonnelConfig, Competence } from '../../../../../../shared/types/config';

interface VehiculeModalProps {
  editingVehicule: VehiculeConfig | null;
  vehiculeForm: Omit<VehiculeConfig, 'id'>;
  setVehiculeForm: React.Dispatch<React.SetStateAction<Omit<VehiculeConfig, 'id'>>>;
  services: Service[];
  /** Grades (config_personnel) du même service : ordre de priorité sur l’engin */
  personnelConfigs: PersonnelConfig[];
  competences: Competence[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

function syncGradePriority(
  form: Omit<VehiculeConfig, 'id'>,
  idGradesPriorite: number[]
): Omit<VehiculeConfig, 'id'> {
  return {
    ...form,
    id_grades_priorite: idGradesPriorite,
    id_grades_armement: idGradesPriorite,
  };
}

const VehiculeModal: React.FC<VehiculeModalProps> = ({
  editingVehicule,
  vehiculeForm,
  setVehiculeForm,
  services,
  personnelConfigs,
  competences,
  onClose,
  onSave,
}) => {
  const [searchCompArmement, setSearchCompArmement] = useState('');
  const [searchGradesPrio, setSearchGradesPrio] = useState('');
  const [gradeToAppend, setGradeToAppend] = useState<number | ''>('');

  const gradesForService = personnelConfigs.filter(
    (p) => p.id_service === vehiculeForm.id_service && p.id != null && vehiculeForm.id_service > 0
  );

  const idGradesPrio = vehiculeForm.id_grades_priorite ?? vehiculeForm.id_grades_armement ?? [];

  const competencesArmement = useMemo(() => {
    return competences.filter(
      (c) =>
        c.id != null &&
        (c.id_service == null || c.id_service === vehiculeForm.id_service) &&
        vehiculeForm.id_service > 0
    );
  }, [competences, vehiculeForm.id_service]);

  const idsCompArmement = vehiculeForm.id_competences_armement ?? [];

  const toggleCompetenceArmement = (competenceId: number, checked: boolean) => {
    const next = checked
      ? [...idsCompArmement, competenceId]
      : idsCompArmement.filter((x) => x !== competenceId);
    setVehiculeForm({
      ...vehiculeForm,
      id_competences_armement: Array.from(new Set(next)),
    });
  };

  const moveGrade = (index: number, dir: -1 | 1) => {
    const next = [...idGradesPrio];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    const t = next[index];
    next[index] = next[j];
    next[j] = t;
    setVehiculeForm(syncGradePriority(vehiculeForm, next));
  };

  const removeGradeAt = (index: number) => {
    const next = idGradesPrio.filter((_, i) => i !== index);
    setVehiculeForm(syncGradePriority(vehiculeForm, next));
  };

  const appendGrade = (personnelId: number) => {
    if (!personnelId || idGradesPrio.includes(personnelId)) return;
    setVehiculeForm(syncGradePriority(vehiculeForm, [...idGradesPrio, personnelId]));
    setGradeToAppend('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white">
            {editingVehicule ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom *</label>
              <input
                type="text"
                value={vehiculeForm.nom}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, nom: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                value={vehiculeForm.type}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, type: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Service *</label>
              <select
                value={Number(vehiculeForm.id_service) > 0 ? Number(vehiculeForm.id_service) : 0}
                onChange={(e) => {
                  const sid = parseInt(e.target.value, 10) || 0;
                  setVehiculeForm({ ...vehiculeForm, id_service: sid });
                }}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.nom}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Niveaux *</label>
              <input
                type="number"
                value={vehiculeForm.niveaux}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, niveaux: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prix *</label>
              <input
                type="number"
                value={vehiculeForm.prix}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, prix: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Kilométrage *</label>
              <input
                type="number"
                value={vehiculeForm.kilometrage}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, kilometrage: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fonction départ *</label>
              <input
                type="text"
                value={vehiculeForm.fonction_depart}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, fonction_depart: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité personnels min *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_min}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_min: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité personnels max *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_max}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_max: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité eau (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_eau}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_eau: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Capacité émulseur (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_emulseur}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_emulseur: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compétences requises pour l&apos;armement (normale ou spécialité) *
                {idsCompArmement.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">
                    ({idsCompArmement.length} sélectionnée{idsCompArmement.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                L&apos;équipier doit posséder ces compétences (config_competence) pour armer ce type d&apos;engin. Incluez une spécialité ici si nécessaire, sans séparer « véhicule spécialiste ».
              </p>
              {!vehiculeForm.id_service ? (
                <p className="text-gray-400 text-sm">Choisissez d&apos;abord un service.</p>
              ) : (
                <>
                  {competencesArmement.length > 8 && (
                    <div className="mb-3 relative">
                      <input
                        type="text"
                        placeholder="Rechercher une compétence..."
                        value={searchCompArmement}
                        onChange={(e) => setSearchCompArmement(e.target.value)}
                        className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <svg
                        className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-56 overflow-y-auto">
                    {competencesArmement.length === 0 ? (
                      <p className="text-gray-400 text-sm">
                        Aucune compétence pour ce périmètre. Créez des compétences (générales ou du service) dans l&apos;onglet Compétences.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {competencesArmement
                          .filter((c) =>
                            searchCompArmement
                              ? c.nom.toLowerCase().includes(searchCompArmement.toLowerCase())
                              : true
                          )
                          .map((c) => (
                            <label
                              key={c.id}
                              className="flex items-start gap-2 p-2 rounded hover:bg-gray-700/50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={c.id != null && idsCompArmement.includes(c.id)}
                                onChange={(e) => c.id != null && toggleCompetenceArmement(c.id, e.target.checked)}
                                className="w-4 h-4 mt-0.5 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500 flex-shrink-0"
                              />
                              <span className="text-sm text-gray-300">
                                {c.nom}
                                {c.is_specialite && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">Spé</span>
                                )}
                              </span>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Priorité des grades sur l&apos;engin
                {idGradesPrio.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">({idGradesPrio.length} en ordre)</span>
                )}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Ordre d&apos;affichage / hiérarchie des postes (chef d&apos;agrès, conducteur, etc.). Cela ne remplace pas les compétences d&apos;armement ci-dessus.
              </p>
              {!vehiculeForm.id_service ? (
                <p className="text-gray-400 text-sm">Choisissez un service pour lister les grades.</p>
              ) : gradesForService.length === 0 ? (
                <p className="text-gray-400 text-sm">Aucun grade pour ce service (onglet Personnels).</p>
              ) : (
                <>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-3 space-y-2 min-h-[3rem]">
                    {idGradesPrio.length === 0 ? (
                      <p className="text-gray-500 text-sm">Aucun ordre défini (optionnel).</p>
                    ) : (
                      idGradesPrio.map((pid, index) => {
                        const label = personnelConfigs.find((p) => p.id === pid)?.grade ?? `ID ${pid}`;
                        return (
                          <div
                            key={`${pid}-${index}`}
                            className="flex items-center gap-2 flex-wrap bg-gray-900/60 rounded px-2 py-1.5 border border-gray-700/80"
                          >
                            <span className="text-xs text-gray-500 w-6 tabular-nums">{index + 1}.</span>
                            <span className="text-sm text-gray-200 flex-1 min-w-[8rem]">{label}</span>
                            <button
                              type="button"
                              title="Monter"
                              disabled={index === 0}
                              onClick={() => moveGrade(index, -1)}
                              className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              title="Descendre"
                              disabled={index === idGradesPrio.length - 1}
                              onClick={() => moveGrade(index, 1)}
                              className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              title="Retirer"
                              onClick={() => removeGradeAt(index)}
                              className="px-2 py-1 text-xs rounded bg-red-900/50 hover:bg-red-800/60 text-red-200"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[12rem]">
                      <label className="block text-xs text-gray-400 mb-1">Ajouter un grade à la fin</label>
                      <select
                        value={gradeToAppend === '' ? '' : String(gradeToAppend)}
                        onChange={(e) => {
                          const v = e.target.value ? parseInt(e.target.value, 10) : '';
                          setGradeToAppend(v === '' ? '' : v);
                        }}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Choisir —</option>
                        {gradesForService
                          .filter((p) => p.id != null && !idGradesPrio.includes(p.id))
                          .filter((p) =>
                            searchGradesPrio
                              ? p.grade.toLowerCase().includes(searchGradesPrio.toLowerCase())
                              : true
                          )
                          .map((p) => (
                            <option key={p.id} value={p.id!}>
                              {p.grade}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      disabled={gradeToAppend === ''}
                      onClick={() => typeof gradeToAppend === 'number' && appendGrade(gradeToAppend)}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium"
                    >
                      Ajouter
                    </button>
                  </div>
                  {gradesForService.length > 6 && (
                    <input
                      type="text"
                      placeholder="Filtrer la liste des grades à ajouter..."
                      value={searchGradesPrio}
                      onChange={(e) => setSearchGradesPrio(e.target.value)}
                      className="mt-2 w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs"
                    />
                  )}
                </>
              )}
            </div>

            <div className="col-span-2 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.specialiste}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, specialiste: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Véhicule spécialiste (indicateur / règles métier côté jeu)
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_remorque}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_remorque: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable remorque
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_lot}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_lot: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable lot
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_cellule}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_cellule: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                />
                Capable cellule
              </label>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            {editingVehicule ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehiculeModal;
